import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  Paperclip,
  RotateCcw,
  Upload,
  UserPlus,
} from 'lucide-react';
import {
  ATTACHMENT_MAX_SIZE_BYTES,
  TICKET_CATEGORY_LABELS,
  TICKET_PRIORITY_LABELS,
  TICKET_PRIORITY_VALUES,
  TICKET_STATUS,
  TICKET_STATUS_LABELS,
  TICKET_STATUS_VALUES,
  type TicketPriority,
  type TicketStatus,
} from '@electrodavisan/shared';
import { ROUTES } from '../../app/config';
import { useCurrentUser } from '../../auth/useSession';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  PageHeader,
  Select,
  Skeleton,
  TicketPriorityBadge,
  TicketStatusBadge,
  useToast,
} from '../../components/ui';
import { formatBytes, formatDateTime } from '../../lib/format';
import { ApiError, downloadFile } from '../../services/api';
import {
  useAssignTicket,
  useChangePriority,
  useChangeStatus,
  useCloseTicket,
  useReopenTicket,
  useTicket,
  useTicketComments,
  useTicketEvents,
  useUploadAttachment,
} from '../../services/tickets.service';
import { TicketConversation } from './TicketConversation';
import { TicketTimeline } from './TicketTimeline';

/**
 * Detalle del ticket, compartido por el area de usuario y el panel de soporte.
 *
 * `scope` solo elige a que endpoint se pregunta. Las notas internas y las
 * acciones de soporte se muestran segun lo que devuelve la API: si el backend
 * no envia una nota interna, aqui no hay nada que ocultar.
 */
export function TicketDetail({
  ticketId,
  scope,
}: {
  ticketId: string;
  scope: 'user' | 'support';
}): JSX.Element {
  const toast = useToast();
  const { data: user } = useCurrentUser();

  const { data: ticket, isLoading, isError, error, refetch } = useTicket(ticketId, scope);
  const { data: comments, isLoading: commentsLoading } = useTicketComments(ticketId, scope);
  const { data: events, isLoading: eventsLoading } = useTicketEvents(ticketId, scope);

  const assign = useAssignTicket();
  const changeStatus = useChangeStatus();
  const changePriority = useChangePriority();
  const close = useCloseTicket();
  const reopen = useReopenTicket();
  const upload = useUploadAttachment();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const isSupportView = scope === 'support' && Boolean(user?.isSupport);
  const backTo = scope === 'support' ? ROUTES.supportTickets : ROUTES.tickets;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !ticket) {
    const apiError = error instanceof ApiError ? error : null;
    return (
      <>
        <PageHeader title="Ticket" />
        <ErrorState
          title={apiError?.status === 404 ? 'Ticket no encontrado' : 'No se pudo cargar el ticket'}
          description={apiError?.message}
          requestId={apiError?.requestId}
          onRetry={() => void refetch()}
        />
      </>
    );
  }

  const handleUpload = (file: File): void => {
    setUploadError(null);
    if (file.size > ATTACHMENT_MAX_SIZE_BYTES) {
      setUploadError(
        `El archivo supera el maximo de ${Math.round(ATTACHMENT_MAX_SIZE_BYTES / 1024 / 1024)} MB.`,
      );
      return;
    }

    upload.mutate(
      { ticketId, file },
      {
        onSuccess: () => toast.success('Adjunto agregado'),
        onError: (uploadFailure) => {
          const message =
            uploadFailure instanceof ApiError
              ? uploadFailure.message
              : 'No se pudo subir el archivo.';
          setUploadError(message);
          toast.error('No se pudo subir el archivo', message);
        },
      },
    );
  };

  const canReopen =
    !isSupportView &&
    (ticket.status === TICKET_STATUS.RESOLVED || ticket.status === TICKET_STATUS.CLOSED);

  /*
   * Cerrar vale desde cualquier estado salvo el que ya esta cerrado, incluso
   * sin que soporte haya contestado: quien abrio el ticket puede haberlo
   * resuelto por su cuenta o haberse equivocado al abrirlo.
   *
   * En RESOLVED salen los dos botones a la vez, y es lo que toca: soporte dice
   * que esta hecho, y el usuario o lo confirma —Cerrar— o lo discute —Reabrir—.
   */
  const canClose = !isSupportView && ticket.status !== TICKET_STATUS.CLOSED;

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link
            to={backTo}
            className="inline-flex items-center gap-1.5 text-[13px] text-text-muted transition-colors hover:text-text-primary"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            {scope === 'support' ? 'Volver a la mesa de ayuda' : 'Volver a mis tickets'}
          </Link>
        }
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-base text-text-muted">{ticket.ticketNumber}</span>
            <span className="min-w-0 break-words">{ticket.subject}</span>
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <TicketStatusBadge status={ticket.status} />
            <TicketPriorityBadge priority={ticket.priority} />
            {canClose ? (
              <Button
                variant="secondary"
                size="sm"
                loading={close.isPending}
                onClick={() =>
                  close.mutate(ticketId, {
                    onSuccess: () => toast.success('Ticket cerrado'),
                    onError: () => toast.error('No se pudo cerrar el ticket'),
                  })
                }
                leftIcon={<CheckCircle2 className="h-4 w-4" aria-hidden />}
              >
                Cerrar
              </Button>
            ) : null}
            {canReopen ? (
              <Button
                variant="secondary"
                size="sm"
                loading={reopen.isPending}
                onClick={() =>
                  reopen.mutate(ticketId, {
                    onSuccess: () => toast.success('Ticket reabierto'),
                    onError: () => toast.error('No se pudo reabrir el ticket'),
                  })
                }
                leftIcon={<RotateCcw className="h-4 w-4" aria-hidden />}
              >
                Reabrir
              </Button>
            ) : null}
          </div>
        }
      />

      {/* Barra de acciones de soporte. */}
      {isSupportView ? (
        <Card className="mb-6">
          <CardHeader
            title="Acciones de soporte"
            description="Estos cambios quedan registrados en la auditoria."
            actions={
              <Button
                size="sm"
                variant={ticket.assignedToSub === user?.sub ? 'secondary' : 'primary'}
                loading={assign.isPending}
                leftIcon={<UserPlus className="h-4 w-4" aria-hidden />}
                onClick={() =>
                  assign.mutate(
                    ticket.assignedToSub === user?.sub
                      ? { ticketId, assigneeSub: null }
                      : { ticketId },
                    {
                      onSuccess: () =>
                        toast.success(
                          ticket.assignedToSub === user?.sub
                            ? 'Asignacion retirada'
                            : 'Ticket asignado a ti',
                        ),
                      onError: () => toast.error('No se pudo actualizar la asignacion'),
                    },
                  )
                }
              >
                {ticket.assignedToSub === user?.sub ? 'Liberar ticket' : 'Tomar ticket'}
              </Button>
            }
          />
          <CardBody className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Estado"
              value={ticket.status}
              onChange={(event) =>
                changeStatus.mutate(
                  { ticketId, status: event.target.value as TicketStatus },
                  {
                    onSuccess: () => toast.success('Estado actualizado'),
                    onError: () => toast.error('No se pudo cambiar el estado'),
                  },
                )
              }
              options={TICKET_STATUS_VALUES.map((value) => ({
                value,
                label: TICKET_STATUS_LABELS[value],
              }))}
            />
            <Select
              label="Prioridad real"
              value={ticket.priority}
              onChange={(event) =>
                changePriority.mutate(
                  { ticketId, priority: event.target.value as TicketPriority },
                  {
                    onSuccess: () => toast.success('Prioridad actualizada'),
                    onError: () => toast.error('No se pudo cambiar la prioridad'),
                  },
                )
              }
              options={TICKET_PRIORITY_VALUES.map((value) => ({
                value,
                label: TICKET_PRIORITY_LABELS[value],
              }))}
              hint={`El usuario la reporto como: ${TICKET_PRIORITY_LABELS[ticket.reportedPriority]}`}
            />
          </CardBody>
        </Card>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-6">
          {/* ------------------------------------------- contenido del ticket */}
          <Card>
            <CardHeader title="Detalle del problema" />
            <CardBody className="space-y-5">
              <Section title="Descripcion">{ticket.description}</Section>
              {ticket.actionPerformed ? (
                <Section title="Que estaba haciendo">{ticket.actionPerformed}</Section>
              ) : null}
              {ticket.stepsToReproduce ? (
                <Section title="Pasos para reproducir">{ticket.stepsToReproduce}</Section>
              ) : null}

              {ticket.errorMessage ? (
                <div>
                  <h3 className="mb-1.5 text-[13px] font-semibold uppercase tracking-wide text-text-muted">
                    Mensaje de error
                  </h3>
                  {/* Monoespaciado: aqui suelen pegarse stack traces. */}
                  <pre className="code-block">{ticket.errorMessage}</pre>
                </div>
              ) : null}

              <div className="grid gap-5 sm:grid-cols-2">
                {ticket.expectedResult ? (
                  <Section title="Resultado esperado">{ticket.expectedResult}</Section>
                ) : null}
                {ticket.actualResult ? (
                  <Section title="Resultado obtenido">{ticket.actualResult}</Section>
                ) : null}
              </div>
            </CardBody>
          </Card>

          {/* -------------------------------------------------------- adjuntos */}
          <Card>
            <CardHeader
              title="Adjuntos"
              description="PNG, JPG, WEBP, PDF, TXT o LOG (maximo 10 MB)."
              actions={
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".png,.jpg,.jpeg,.webp,.pdf,.txt,.log"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) handleUpload(file);
                      event.target.value = '';
                    }}
                  />
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={upload.isPending}
                    onClick={() => fileInputRef.current?.click()}
                    leftIcon={<Upload className="h-4 w-4" aria-hidden />}
                  >
                    Subir archivo
                  </Button>
                </>
              }
            />
            <CardBody>
              {uploadError ? (
                <p role="alert" className="mb-3 rounded-lg bg-danger-soft p-3 text-[13px] text-danger">
                  {uploadError}
                </p>
              ) : null}

              {ticket.attachments.length === 0 ? (
                <p className="text-sm text-text-muted">Este ticket no tiene adjuntos.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {ticket.attachments.map((attachment) => (
                    <li
                      key={attachment.id}
                      className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
                    >
                      <Paperclip className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-text-primary">
                          {attachment.originalName}
                        </p>
                        <p className="text-[12px] text-text-muted">
                          {formatBytes(attachment.sizeBytes)} ·{' '}
                          {formatDateTime(attachment.createdAt)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          void downloadFile(
                            `/api/tickets/${ticketId}/attachments/${attachment.id}`,
                            attachment.originalName,
                          ).catch(() => toast.error('No se pudo descargar el adjunto'));
                        }}
                        leftIcon={<Download className="h-4 w-4" aria-hidden />}
                      >
                        Descargar
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          {/* ---------------------------------------------------- conversacion */}
          <Card>
            <CardHeader title="Conversacion" />
            <CardBody>
              <TicketConversation
                ticketId={ticketId}
                comments={comments}
                isLoading={commentsLoading}
                canWriteInternal={Boolean(user?.isSupport) && scope === 'support'}
                scope={scope}
                currentUserSub={user?.sub}
                disabled={!isSupportView && ticket.status === TICKET_STATUS.CLOSED}
                disabledReason="Este ticket esta cerrado. Reabrelo para continuar la conversacion."
              />
            </CardBody>
          </Card>
        </div>

        {/* -------------------------------------------------------- lateral */}
        <div className="space-y-6">
          <Card>
            <CardHeader title="Informacion" />
            <CardBody className="space-y-3 text-sm">
              <Meta label="Aplicacion" value={ticket.applicationName ?? 'General'} />
              <Meta label="Categoria" value={TICKET_CATEGORY_LABELS[ticket.category]} />
              <Meta label="Creado" value={formatDateTime(ticket.createdAt)} />
              <Meta label="Ultima actividad" value={formatDateTime(ticket.lastActivityAt)} />
              <Meta label="Usuario" value={ticket.createdByName ?? ticket.createdBySub} />
              {isSupportView ? (
                <Meta label="Email" value={ticket.createdByEmail ?? '-'} />
              ) : null}
              <Meta label="Agente asignado" value={ticket.assignedToName ?? 'Sin asignar'} />
              {ticket.resolvedAt ? (
                <Meta label="Resuelto" value={formatDateTime(ticket.resolvedAt)} />
              ) : null}
            </CardBody>
          </Card>

          {/* Contexto tecnico capturado automaticamente. */}
          <Card>
            <CardHeader title="Contexto tecnico" />
            <CardBody className="space-y-3 text-sm">
              <Meta label="Navegador" value={ticket.technical.browser ?? '-'} />
              <Meta label="Sistema operativo" value={ticket.technical.operatingSystem ?? '-'} />
              <Meta label="Origen" value={ticket.technical.originUrl ?? '-'} breakAll />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Historial" />
            <CardBody>
              <TicketTimeline events={events} isLoading={eventsLoading} />
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
}

function Section({ title, children }: { title: string; children: string }): JSX.Element {
  return (
    <div>
      <h3 className="mb-1.5 text-[13px] font-semibold uppercase tracking-wide text-text-muted">
        {title}
      </h3>
      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-text-secondary">
        {children}
      </p>
    </div>
  );
}

function Meta({
  label,
  value,
  breakAll,
}: {
  label: string;
  value: string;
  breakAll?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[12px] uppercase tracking-wide text-text-muted">{label}</span>
      <span className={breakAll ? 'break-all text-text-primary' : 'text-text-primary'}>
        {value}
      </span>
    </div>
  );
}
